/*
 * @flow
 */
import React from 'react';
import moment from 'moment';
import { Button, ButtonGroup, FlexCol, FlexRow, Heading, Icons, TextArea } from 'sarsaparilla';
import Modal from 'react-modal';
import StarDisplay from './StarDisplay';
import CellCoverageIcon from './CellCoverageIcon';
import { describeCoverage } from '../utils/cellCoverage';
import type { publicReview } from '../model/publicReview';

type PropTypes = {
  review: publicReview,
  flagReview: Function,
  voteReview: Function,
};

type State = {
  showModal: boolean,
  flagFeedback: string,
  showMore: boolean,
  summary: string,
  helpfulVotesCount: number,
  notHelpfulVotesCount: number,
};

export default class PublicReview extends React.PureComponent<PropTypes, State> {
  constructor(props: PropTypes) {
    super(props);
    if (props.review.review.length > 200) {
      this.state.summary = `${props.review.review.substring(0, 200)}...`;
    }
  }

  state = {
    showModal: false,
    flagFeedback: '',
    showMore: false,
    summary: '',
    helpfulVotesCount: this.props.review.helpful_votes_count,
    notHelpfulVotesCount: this.props.review.not_helpful_votes_count,
  };

  toggleShowMore() {
    const { showMore } = this.state;
    this.setState({ showMore: !showMore });
  }

  toggleShowModal() {
    const { showModal } = this.state;
    this.setState({ showModal: !showModal });
  }

  reviewText() {
    const {
      review: { review },
    } = this.props;
    const { summary, showMore } = this.state;
    if (!summary || showMore) {
      return review;
    }
    return summary;
  }

  handleVote(is_helpful: boolean, vote_value: string) {
    const {
      review: { reservation_id },
      voteReview,
    } = this.props;
    let newHelpfulVotesCounts = this.state.helpfulVotesCount;
    let newNotHelpfulVotesCounts = this.state.notHelpfulVotesCount;
    let vote_changed = false;

    if (vote_value != null) {
      vote_changed = true;

      if (is_helpful === true) {
        newHelpfulVotesCounts += 1;
        newNotHelpfulVotesCounts -= 1;
      } else {
        newHelpfulVotesCounts -= 1;
        newNotHelpfulVotesCounts += 1;
      }
    } else if (vote_value == null) {
      if (is_helpful === true) {
        newHelpfulVotesCounts += 1;
      } else {
        newNotHelpfulVotesCounts += 1;
      }
    }

    this.setState({ helpfulVotesCount: newHelpfulVotesCounts, notHelpfulVotesCount: newNotHelpfulVotesCounts });
    voteReview({ reservation_id, is_helpful, vote_changed });
  }

  handleFlag() {
    const {
      review: { reservation_id },
      flagReview,
    } = this.props;
    const { flagFeedback: reason } = this.state;
    flagReview({ reservation_id, reason });
    this.toggleShowModal();
  }

  renderDescription() {
    const { review } = this.props;
    const resInfo = review.reservation_info ? review.reservation_info : {};
    switch (review.location_type) {
      case 'Campground':
      case 'Campsite':
        if (!resInfo.product_name || !resInfo.product_description) {
          return null;
        }
        return (
          <div>
            <strong>Loop: </strong> {resInfo.product_name},<strong> Site: </strong> {resInfo.product_description}
          </div>
        );
      case 'Tour':
        return (
          <div>
            <strong>Tour Name: </strong> {resInfo.facility_description}
          </div>
        );
      case 'Permit':
        return (
          <div>
            <strong>Zone: </strong> {resInfo.facility_name}
          </div>
        );
      default:
        return null;
    }
  }

  renderDates() {
    const { review } = this.props;
    const resInfo = review.reservation_info ? review.reservation_info : {};
    switch (review.location_type) {
      case 'Campground':
      case 'Permit':
      default:
        return (
          <div>
            <strong>Reservation Dates: </strong> {moment(resInfo.start_date).format('M/D/YYYY')} -{' '}
            {moment(resInfo.end_date).format('M/D/YYYY')}
          </div>
        );
      case 'Tour':
        return (
          <div>
            <strong>Tour Date: </strong> {moment(resInfo.start_date).format('M/D/YYYY')}
          </div>
        );
    }
  }

  render() {
    const { review } = this.props;
    const { showMore, summary, showModal, flagFeedback, helpfulVotesCount, notHelpfulVotesCount } = this.state;
    const voteValue = localStorage.getItem(review.reservation_id);

    return (
      <div className="public-review-wrapper">
        <StarDisplay value={parseInt(review.rating, 10)} />

        <FlexRow justifyContent="space-between" alignItems="center" hasGutters={false} className="heading-row">
          <FlexCol xs={12} md={4}>
            <Heading headingLevel={5} appearance="h5" className="mb-0">
              {review.first_name} {review.last_initial}
            </Heading>
          </FlexCol>
          <FlexCol xs={12} md="variable" className="submitted-by">
            Submitted on {moment(review.created_at).format('M/D/YYYY')}
          </FlexCol>
        </FlexRow>
        {this.renderDescription()}
        {this.renderDates()}

        <pre>{this.reviewText()}</pre>
        {!!summary &&
          (showMore ? (
            <Button
              appearance="link"
              iconBeforeElement={<Icons.IconChevronUp />}
              onClick={this.toggleShowMore.bind(this)}
              className="show-more-toggle"
            >
              Show Less
            </Button>
          ) : (
            <Button
              appearance="link"
              iconBeforeElement={<Icons.IconChevronDown />}
              onClick={this.toggleShowMore.bind(this)}
              className="show-more-toggle"
            >
              Show More
            </Button>
          ))}
        {review.cell_coverage_ratings &&
          review.cell_coverage_ratings.map((row, i) => (
            <FlexRow
              className="cell-row-wrap"
              justifyContent="space-around"
              alignItems="center"
              key={`cell-rating-${i}`}
            >
              <FlexCol xs={12} md={5} lg={12} xl={5} className="cell-row">
                <Icons.IconCellCarrier size="sm" /> <strong>Phone Carrier: </strong> {row.carrier}
              </FlexCol>
              <FlexCol xs={12} md={7} lg={12} xl={7} className="cell-row">
                <CellCoverageIcon value={parseInt(row.rating, 10)} size="sm" /> <strong>Coverage Strength: </strong>{' '}
                {`(${row.rating}) `}
                {` ${describeCoverage(row.rating)}`}
              </FlexCol>
            </FlexRow>
          ))}

        <ButtonGroup isFullWidthOnMobile={false}>
          <i className="mr-2">Was this review helpful?</i>
          <Button
            appearance="tertiary"
            size="xs"
            isDisabled={voteValue === 'true'}
            onClick={() => this.handleVote.bind(this)(true, voteValue)}
            screenReaderTextBefore="Vote review helpful"
          >
            {`Yes (${helpfulVotesCount})`}
          </Button>
          <Button
            appearance="tertiary"
            size="xs"
            isDisabled={voteValue === 'false'}
            onClick={() => this.handleVote.bind(this)(false, voteValue)}
            screenReaderTextBefore="Vote review unhelpful"
          >
            {`No (${notHelpfulVotesCount})`}
          </Button>
          <Button
            id={`flag-review-button-${review.created_at}`}
            appearance="tertiary"
            size="xs"
            onClick={this.toggleShowModal.bind(this)}
            iconBeforeElement={<Icons.IconFlag />}
            screenReaderTextBefore="Flag review"
          />
        </ButtonGroup>
        <Modal
          id="public-review-flag-modal"
          role="complementary"
          className={{
            base: 'rec-modal-dialog-base',
            afterOpen: 'rec-modal-dialog-after-open-md',
            beforeClose: 'rec-modal-dialog-before-close',
          }}
          overlayClassName={{
            base: 'rec-overlay-base',
            afterOpen: 'rec-overlay-after-open',
            beforeClose: 'rec-overlay-before-close',
          }}
          onRequestClose={this.toggleShowModal.bind(this)}
          isOpen={showModal}
        >
          <div className="rec-modal-content-wrap">
            <Button
              className="rec-close-modal"
              appearance="subtle"
              size="sm"
              iconBeforeElement={<Icons.IconClose />}
              screenReaderTextBefore="Close the modal"
              onClick={this.toggleShowModal.bind(this)}
            />
            <TextArea
              placeholder="Enter feedback here"
              label="Tell us why you find this review inappropriate"
              isRequired
              errorText="Max character count: 300"
              value={flagFeedback}
              onChange={({ target: { value } }) => this.setState({ flagFeedback: value })}
              id="flag-reason-input"
            />
          </div>
          <div className="rec-modal-actions-wrap">
            <Button appearance="primary" isDisabled={flagFeedback.length < 1} onClick={this.handleFlag.bind(this)}>
              Submit
            </Button>
          </div>
        </Modal>
      </div>
    );
  }
}
